import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type Paginated } from '@/lib/api';

export interface BoxSlot {
  id: number;
  box_id: number;
  row: number;
  col: number;
  wine_id: number | null;
  status: 'empty' | 'planned' | 'confirmed';
  notes: string | null;
  wine?: {
    id: number;
    name: string;
    vintage: number | null;
    format: string;
    winery: string | null;
    image_url: string | null;
  };
}

/** Una línea del detalle de vinos de una caja (agrupado por vino). */
export interface BoxWineLine {
  wine_id: number;
  name: string;
  winery: string | null;
  vintage: number | null;
  format: string | null;
  deleted: boolean;
  qty: number;
  purchase_date: string | null;
  unit_cost_usd: number | null;
  market_usd: number | null;
  subtotal_cost_usd: number | null;
  subtotal_market_usd: number | null;
}

export interface BoxTotals {
  bottles: number;
  cost_usd: number | null;
  market_usd: number | null;
}

export interface Box {
  id: number;
  box_number: string;
  rows: number;
  cols: number;
  location_id: number | null;
  status: 'draft' | 'confirmed';
  notes: string | null;
  slots?: BoxSlot[];
  slots_count?: number;
  planned_count?: number;
  confirmed_count?: number;
  empty_count?: number;
  // Slots con wine borrado o sin stock.
  problem_count?: number;
  // Detalle de vinos + totales (solo en el listado). Ver BoxResource.
  wine_lines?: BoxWineLine[] | null;
  totals?: BoxTotals | null;
  created_at: string;
  updated_at: string;
}

/** Lista las cajas físicas donde aparece un wine específico. */
export function useWineBoxes(wineId: number | undefined) {
  return useQuery({
    enabled: !!wineId,
    queryKey: ['boxes', 'for-wine', wineId],
    queryFn: async () => {
      const { data } = await api.get<{ data: Box[] }>(`/wines/${wineId}/boxes`);
      return data.data;
    },
  });
}

export function useBoxes(
  params: { status?: 'draft' | 'confirmed'; needs_review?: 1 } = {},
) {
  return useQuery({
    queryKey: ['boxes', params],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Box>>('/boxes', { params: { ...params, per_page: 200 } });
      return data.data;
    },
  });
}

export function useBox(id: number | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['box', id],
    queryFn: async () => {
      const { data } = await api.get<{ data: Box }>(`/boxes/${id}`);
      return data.data;
    },
  });
}

export function useCreateBox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { box_number: string; rows: number; cols: number; notes?: string }) => {
      const { data } = await api.post<{ data: Box }>('/boxes', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boxes'] }),
  });
}

export function useUpdateBox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Box> }) => {
      const { data } = await api.put<{ data: Box }>(`/boxes/${id}`, payload);
      return data.data;
    },
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ['boxes'] });
      qc.invalidateQueries({ queryKey: ['box', b.id] });
    },
  });
}

export function useDeleteBox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/boxes/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boxes'] }),
  });
}

export function useSetSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      boxId, row, col, wine_id, status, notes,
    }: {
      boxId: number;
      row: number;
      col: number;
      wine_id: number | null;
      status: BoxSlot['status'];
      notes?: string | null;
    }) => {
      const { data } = await api.patch<{ data: BoxSlot }>(`/boxes/${boxId}/slot`, {
        row, col, wine_id, status, notes: notes ?? null,
      });
      return data.data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['box', vars.boxId] }),
  });
}

export function useConfirmBox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ data: Box }>(`/boxes/${id}/confirm`);
      return data.data;
    },
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ['boxes'] });
      qc.invalidateQueries({ queryKey: ['box', b.id] });
    },
  });
}

export function useReopenBox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ data: Box }>(`/boxes/${id}/reopen`);
      return data.data;
    },
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ['boxes'] });
      qc.invalidateQueries({ queryKey: ['box', b.id] });
    },
  });
}
